import type { CupaInput } from "generated-graphql/graphql";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { useForm } from "react-hook-form";

import { CREATE_CUPA, GET_CUPA, UPDATE_CUPA } from "./schema";

type CupaDialogProps = {
  onClose: () => void;
  mode: "add" | "edit";
  cupaId?: string;
};

export function CupaDialog({ onClose, mode, cupaId }: CupaDialogProps) {
  const alerts = useAlerts();

  const { data: cupaData, loading: isLoadingCupa } = useQuery(GET_CUPA, {
    fetchPolicy: "cache-and-network",
    variables: { id: cupaId ?? "" },
    skip: !cupaId,
  });

  // Refetch by operation name so the grid's active `cupas` query (which runs
  // with search/page/sort variables) is refreshed. A document-only descriptor
  // refetches with undefined variables, landing on a different cache key and
  // leaving the grid stale.
  const [createCupa, { loading: isCreating }] = useMutation(CREATE_CUPA, {
    refetchQueries: ["cupas"],
  });
  const [updateCupa, { loading: isUpdating }] = useMutation(UPDATE_CUPA, {
    refetchQueries: ["cupas"],
  });

  const cupa = cupaData?.cupa;

  const form = useForm<CupaInput>({
    mode: "onChange",
    defaultValues: {
      cupaCode: cupa?.cupaCode ?? "",
      displayName: cupa?.displayName ?? "",
      districtName: cupa?.districtName ?? "",
      county: cupa?.county ?? "",
      notes: cupa?.notes ?? "",
    },
    values: cupa
      ? {
          cupaCode: cupa.cupaCode,
          displayName: cupa.displayName,
          districtName: cupa.districtName,
          county: cupa.county ?? "",
          notes: cupa.notes ?? "",
        }
      : undefined,
  });

  const { handleSubmit, control, getValues, formState } = form;
  const savingOrLoading = (mode === "edit" && isLoadingCupa) || isCreating || isUpdating;

  const onSubmit = async () => {
    const input = { ...getValues() };
    if (mode === "add") {
      await createCupa({
        variables: { input },
        onCompleted: () => {
          onClose();
          alerts.success("CUPA created successfully");
        },
        onError: (error) => {
          alerts.error("Failed to create CUPA", error);
        },
      });
    } else if (cupaId) {
      await updateCupa({
        variables: { id: cupaId, input },
        onCompleted: () => {
          onClose();
          alerts.success("CUPA updated successfully");
        },
        onError: (error) => {
          alerts.error("Failed to update CUPA", error);
        },
      });
    }
  };

  const title = mode === "add" ? "Add CUPA" : "Edit CUPA";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={title}
      primaryActionButtonProps={{
        loading: savingOrLoading,
        onClick: handleSubmit(onSubmit),
        disabled: !formState.isValid,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <FormTextField
              name="cupaCode"
              label="CUPA Code"
              control={control}
              rules={{ required: "CUPA code is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 8 }}>
            <FormTextField name="county" label="County" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="displayName"
              label="Display Name"
              control={control}
              rules={{ required: "Display name is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="districtName"
              label="District Name"
              control={control}
              rules={{ required: "District name is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField name="notes" label="Notes" control={control} />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}
